/**
 * Every visible string in the chat widget, EN + ES. Voice per .claude/rules/copy.md: plain
 * words, no hype, no time-to-reply promises. The assistant is named as an AI in the subtitle
 * and in its first line — Jorge's photo is on the widget because Jorge is the person who
 * follows up, never to suggest he is typing.
 */

export type Language = "en" | "es";

export type ChipId = "website" | "google" | "photography" | "book" | "talk";

export interface Chip {
  id: ChipId;
  label: string;
  /** Sent as the visitor's first message. Absent for chips that navigate or open the card. */
  message?: string;
}

export interface CardFieldCopy {
  name: string;
  email: string;
  phone: string;
  business: string;
  message: string;
  site: string;
}

/** The two taps of context the note carries when the assistant is away: no model, no free text, one tap each. */
export type AboutId = "website" | "google" | "photography" | "other";
export type TimingId = "now" | "soon" | "looking";
export interface ContextChip<T extends string = string> {
  id: T;
  label: string;
}

export interface ChatCopy {
  launcher: string;
  launcherAria: string;
  teaser: string;
  teaserClose: string;
  title: string;
  subtitle: string;
  intro: string;
  chips: Chip[];
  composerLabel: string;
  composerPlaceholder: string;
  composerFormMode: string;
  send: string;
  /** Under a visitor message that never reached the assistant. */
  notSent: string;
  thinking: string;
  charsLeft: (n: number) => string;
  cardOfferTitle: string;
  cardOfferBody: string;
  cardRequiredTitle: string;
  cardRequiredBody: string;
  formModeNotice: string;
  capNotice: string;
  errorNotice: string;
  /** The note's optional context: a title, two chip rows, and how each answer is labelled in the note Jorge reads. */
  contextTitle: string;
  contextAbout: string;
  contextTiming: string;
  aboutChips: ContextChip<AboutId>[];
  timingChips: ContextChip<TimingId>[];
  noteLabels: { about: string; timing: string; site: string };
  /** After the note is saved: the footer line, and where to go while Jorge reads it. */
  formDone: string;
  doneTitle: string;
  doneBook: string;
  topicLinks: Partial<Record<AboutId, { label: string; href: string }>>;
  fields: CardFieldCopy;
  cardSubmit: string;
  cardSubmitting: string;
  cardLater: string;
  leaveDetails: string;
  saved: string;
  errors: {
    name: string;
    email: string;
    phone: string;
    site: string;
    generic: string;
    network: string;
  };
  footerAi: string;
  footerPrivacy: string;
  footerForm: string;
  close: string;
  liveOpened: string;
  liveReply: string;
  liveThinking: string;
  liveSaved: string;
  bookHref: string;
  contactHref: string;
  privacyHref: string;
  you: string;
  assistant: string;
}

/** Routes that are already the form; the widget stays off them. */
export const EXCLUDED_ROUTES = ["/contact", "/es/contacto", "/book", "/book/thank-you", "/es/agendar", "/es/agendar/gracias"];

export const CHAT_POPUP_ID = "chat-widget";
export const KICK_POPUP_ID = "kick-promo";
export const TEASER_SESSION_KEY = "mb_chat_teaser";

export const PORTRAIT_BASE = "/images/jorge-giraldez/jorge-giraldez";

export const MAX_MESSAGE_CHARS = 500;

const en: ChatCopy = {
  launcher: "Message Jorge",
  launcherAria: "Message Jorge — open the chat",
  teaser: "Have a question about your website or getting found on Google? Ask here — Jorge follows up personally.",
  teaserClose: "Dismiss",
  title: "Jorge Giraldez",
  subtitle: "MarketBrewer · AI assistant — Jorge follows up personally",
  intro: "Hi — I'm Jorge's AI assistant. Ask me anything about websites, SEO, Google Maps, or photography for your business. Jorge reads every conversation and follows up personally.",
  chips: [
    { id: "website", label: "Website", message: "I'm interested in a new website for my business." },
    { id: "google", label: "Getting found on Google", message: "I want my business to show up on Google." },
    { id: "photography", label: "Photography", message: "Tell me about photography for my business." },
    { id: "book", label: "Book a call" },
    { id: "talk", label: "Talk to Jorge" },
  ],
  composerLabel: "Your message",
  composerPlaceholder: "Type your question…",
  composerFormMode: "Use the card above to reach Jorge.",
  send: "Send",
  notSent: "Not sent",
  thinking: "Thinking…",
  charsLeft: (n) => `${n} characters left`,
  cardOfferTitle: "Want Jorge to follow up?",
  cardOfferBody: "Leave your details and he'll reply by email.",
  cardRequiredTitle: "Leave Jorge a note",
  cardRequiredBody: "He reads it himself and writes back by email. Anything you typed above goes with it.",
  formModeNotice: "The assistant is taking a break right now, so it's Jorge from here. Leave him a note below — he reads every one himself and writes back by email.",
  capNotice: "That's as far as the assistant goes in one chat. From here it's Jorge: leave him a note below and he picks it up himself.",
  errorNotice: "That one didn't go through. Try again, or leave it as a note for Jorge below — he reads every one himself.",
  contextTitle: "Two taps so Jorge can look before he writes back (optional)",
  contextAbout: "What's it about?",
  contextTiming: "How soon?",
  aboutChips: [
    { id: "website", label: "A website" },
    { id: "google", label: "Getting found on Google" },
    { id: "photography", label: "Photography" },
    { id: "other", label: "Something else" },
  ],
  timingChips: [
    { id: "now", label: "This month" },
    { id: "soon", label: "In the next few months" },
    { id: "looking", label: "Just looking" },
  ],
  noteLabels: { about: "About", timing: "Timing", site: "Site" },
  formDone: "Jorge has your note. Close this whenever you like.",
  doneTitle: "While Jorge reads it",
  doneBook: "Pick a time to talk",
  topicLinks: {
    website: { label: "How Jorge builds a website", href: "/services/website-development" },
    google: { label: "Getting found on Google Maps", href: "/services/google-business-profile" },
    photography: { label: "Photography for your business", href: "/services/photography" },
  },
  fields: {
    name: "Name",
    email: "Email",
    phone: "Phone (optional)",
    business: "Business (optional)",
    message: "Anything else for Jorge? (optional)",
    site: "Your website or Google listing (optional)",
  },
  cardSubmit: "Send to Jorge",
  cardSubmitting: "Sending…",
  cardLater: "Maybe later",
  leaveDetails: "Leave your details",
  saved: "Got it — Jorge has your note and writes back by email himself. Nothing else to do on your end.",
  errors: {
    name: "Please enter your name.",
    email: "Please enter a valid email.",
    phone: "Please check the phone number.",
    site: "Please check the address — a domain like example.com is enough.",
    generic: "Something went wrong. Please try again.",
    network: "Couldn't reach the server. Check your connection and try again.",
  },
  footerAi: "Answers are AI-generated. Messages are saved so Jorge can follow up.",
  footerPrivacy: "Privacy",
  footerForm: "Prefer a form? Contact page",
  close: "Close the chat",
  liveOpened: "Chat opened. Jorge's AI assistant. Jorge follows up personally.",
  liveReply: "New reply from the assistant.",
  liveThinking: "The assistant is thinking.",
  liveSaved: "Your details were sent to Jorge.",
  bookHref: "/book",
  contactHref: "/contact",
  privacyHref: "/privacy-policy",
  you: "You",
  assistant: "Assistant",
};

const es: ChatCopy = {
  launcher: "Escríbele a Jorge",
  launcherAria: "Escríbele a Jorge — abrir el chat",
  teaser: "¿Tienes una pregunta sobre tu sitio web o cómo aparecer en Google? Pregunta aquí — Jorge te responde personalmente.",
  teaserClose: "Cerrar",
  title: "Jorge Giraldez",
  subtitle: "MarketBrewer · asistente de IA — Jorge te responde personalmente",
  intro: "Hola — soy el asistente de IA de Jorge. Pregúntame lo que quieras sobre sitios web, SEO, Google Maps o fotografía para tu negocio. Jorge lee cada conversación y te responde personalmente.",
  chips: [
    { id: "website", label: "Sitio web", message: "Me interesa un sitio web nuevo para mi negocio." },
    { id: "google", label: "Aparecer en Google", message: "Quiero que mi negocio aparezca en Google." },
    { id: "photography", label: "Fotografía", message: "Cuéntame sobre fotografía para mi negocio." },
    { id: "book", label: "Agendar una llamada" },
    { id: "talk", label: "Hablar con Jorge" },
  ],
  composerLabel: "Tu mensaje",
  composerPlaceholder: "Escribe tu pregunta…",
  composerFormMode: "Usa la tarjeta de arriba para contactar a Jorge.",
  send: "Enviar",
  notSent: "No se envió",
  thinking: "Pensando…",
  charsLeft: (n) => `Quedan ${n} caracteres`,
  cardOfferTitle: "¿Quieres que Jorge te responda?",
  cardOfferBody: "Deja tus datos y te escribe por correo.",
  cardRequiredTitle: "Déjale una nota a Jorge",
  cardRequiredBody: "Él la lee personalmente y te responde por correo. Lo que escribiste arriba va con ella.",
  formModeNotice: "El asistente está en pausa ahora mismo, así que desde aquí sigue Jorge. Déjale una nota abajo — él lee cada una personalmente y te responde por correo.",
  capNotice: "Hasta aquí llega el asistente en una conversación. Desde aquí sigue Jorge: déjale una nota abajo y él la toma personalmente.",
  errorNotice: "Eso no se envió. Intenta de nuevo, o déjaselo como nota a Jorge abajo — él lee cada una personalmente.",
  contextTitle: "Dos toques para que Jorge pueda mirar antes de responderte (opcional)",
  contextAbout: "¿De qué se trata?",
  contextTiming: "¿Para cuándo?",
  aboutChips: [
    { id: "website", label: "Un sitio web" },
    { id: "google", label: "Aparecer en Google" },
    { id: "photography", label: "Fotografía" },
    { id: "other", label: "Otra cosa" },
  ],
  timingChips: [
    { id: "now", label: "Este mes" },
    { id: "soon", label: "En los próximos meses" },
    { id: "looking", label: "Solo estoy mirando" },
  ],
  noteLabels: { about: "Tema", timing: "Plazo", site: "Sitio" },
  formDone: "Jorge ya tiene tu nota. Cierra esto cuando quieras.",
  doneTitle: "Mientras Jorge la lee",
  doneBook: "Elige una hora para hablar",
  topicLinks: {
    website: { label: "Cómo Jorge construye un sitio web", href: "/es/servicios/desarrollo-web" },
    google: { label: "Aparecer en Google Maps", href: "/es/servicios/google-business-profile" },
    photography: { label: "Fotografía para tu negocio", href: "/es/servicios/fotografia" },
  },
  fields: {
    name: "Nombre",
    email: "Correo",
    phone: "Teléfono (opcional)",
    business: "Negocio (opcional)",
    message: "¿Algo más para Jorge? (opcional)",
    site: "Tu sitio web o tu ficha de Google (opcional)",
  },
  cardSubmit: "Enviar a Jorge",
  cardSubmitting: "Enviando…",
  cardLater: "Quizás después",
  leaveDetails: "Dejar mis datos",
  saved: "Listo — Jorge ya tiene tu nota y te responde por correo personalmente. No tienes que hacer nada más.",
  errors: {
    name: "Escribe tu nombre.",
    email: "Escribe un correo válido.",
    phone: "Revisa el número de teléfono.",
    site: "Revisa la dirección — con un dominio como ejemplo.com basta.",
    generic: "Algo salió mal. Intenta de nuevo.",
    network: "No se pudo conectar. Revisa tu conexión e intenta de nuevo.",
  },
  footerAi: "Las respuestas las genera una IA. Los mensajes se guardan para que Jorge te responda.",
  footerPrivacy: "Privacidad",
  footerForm: "¿Prefieres un formulario? Página de contacto",
  close: "Cerrar el chat",
  liveOpened: "Chat abierto. Asistente de IA de Jorge. Jorge te responde personalmente.",
  liveReply: "Nueva respuesta del asistente.",
  liveThinking: "El asistente está pensando.",
  liveSaved: "Tus datos se enviaron a Jorge.",
  bookHref: "/es/agendar",
  contactHref: "/es/contacto",
  privacyHref: "/es/politica-de-privacidad",
  you: "Tú",
  assistant: "Asistente",
};

export const COPY: Record<Language, ChatCopy> = { en, es };
